import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BaseModal } from '../ui/BaseModal';
import { VALIDATION_PATTERNS } from '../../constants/validation';

interface SirenUpdateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (correctedSiren: string) => Promise<void>;
  currentSiren: string;
  isLoading?: boolean;
}

export const SirenUpdateModal = ({
  isOpen,
  onClose,
  onSubmit,
  currentSiren,
  isLoading = false,
}: SirenUpdateModalProps) => {
  const { t } = useTranslation();
  const [correctedSiren, setCorrectedSiren] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    const cleanedSiren = correctedSiren.replace(/\s/g, '');

    if (!cleanedSiren) {
      setError(t('company.siren.error.required'));
      return;
    }

    if (!VALIDATION_PATTERNS.SIREN.test(cleanedSiren)) {
      setError(t('company.siren.error.invalid'));
      return;
    }

    if (cleanedSiren === currentSiren) {
      setError("Le nouveau SIREN doit être différent de l'actuel");
      return;
    }

    try {
      await onSubmit(cleanedSiren);
      setCorrectedSiren('');
      setError('');
    } catch (err) {
      setError(t('common.error'));
    }
  };

  const handleClose = () => {
    setCorrectedSiren('');
    setError('');
    onClose();
  };

  const footer = (
    <div className="flex gap-4">
      <button
        type="button"
        onClick={handleClose}
        className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition"
        disabled={isLoading}
      >
        {t('company.siren_update_modal.cancel')}
      </button>
      <button
        type="submit"
        form="siren-form"
        className="flex-1 px-4 py-2 bg-primary text-white rounded-md hover:bg-opacity-90 transition disabled:opacity-50"
        disabled={isLoading}
      >
        {t('company.siren_update_modal.submit')}
      </button>
    </div>
  );

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={handleClose}
      title={t('company.siren_update_modal.title')}
      footer={footer}
      maxWidth="2xl"
    >
      <form id="siren-form" onSubmit={handleSubmit}>
        <p className="text-sm text-gray-600 mb-6">{t('company.siren_update_modal.subtitle')}</p>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('company.siren.label')} {t('common.current')}
            </label>
            <input
              type="text"
              value={currentSiren}
              disabled
              className="w-full px-4 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-500 cursor-not-allowed"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('company.siren_update_modal.corrected_siren.label')} *
            </label>
            <input
              type="text"
              value={correctedSiren}
              onChange={(e) => setCorrectedSiren(e.target.value)}
              placeholder={t('company.siren_update_modal.corrected_siren.placeholder')}
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
              required
              disabled={isLoading}
            />
            {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
          </div>
        </div>
      </form>
    </BaseModal>
  );
};
